import React, { useContext, useEffect, useState } from 'react';
import { Box, Flex, Text, Switch, useToast } from '@chakra-ui/react';
import { FiCalendar } from 'react-icons/fi';
import { influencerApi } from 'services/api/influencer';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import { ff } from '../../../theme';

const CALENDAR_INVITES = 'calendarInvites';

interface NotificationsEditProps {
  onBack: () => void;
}

const NotificationsEdit: React.FC<NotificationsEditProps> = ({ onBack }) => {
  const toast = useToast();
  const { influencerData } = useContext(InfluencerDataContext);
  const savedOptOut = (influencerData?.unsubscribedEmails || []).includes(CALENDAR_INVITES);
  const [optOut, setOptOut] = useState<boolean>(savedOptOut);
  const [isSaving, setIsSaving] = useState(false);

  // Reconcile with the live influencer doc (context is an onSnapshot listener)
  useEffect(() => {
    setOptOut(savedOptOut);
  }, [savedOptOut]);

  /**
   * Autosave the calendar-invite opt-out, preserving any other names in
   * unsubscribedEmails
   */
  const handleToggle = async (checked: boolean) => {
    if (!influencerData?.id || isSaving) return;

    setOptOut(checked);
    setIsSaving(true);
    try {
      const current = influencerData.unsubscribedEmails || [];
      const updated = checked
        ? Array.from(new Set([...current, CALENDAR_INVITES]))
        : current.filter((name) => name !== CALENDAR_INVITES);

      await influencerApi.updateProfile({ unsubscribedEmails: updated }, influencerData.id);
    } catch (error) {
      console.error('Error saving notification preference:', error);
      // Revert to last saved value
      setOptOut(!checked);
      toast({
        title: "Couldn't save notification preference",
        description: 'Reverted to last saved value',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Board: "How we reach you" card. The switch reads as "on = send me invites", so it is the
  // inverse of the stored opt-out.
  return (
    <Box as="section" aria-labelledby="how-we-reach-you" border="1px solid" borderColor={ff.border} borderRadius="16px" px="16px" py="4px">
      <Text as="h2" id="how-we-reach-you" m="12px 0 4px" textStyle="eyebrow" fontSize="13px" letterSpacing="0.04em" color={ff.text2}>
        How we reach you
      </Text>
      <Flex align="center" justify="space-between" gap="12px" py="10px">
        <Flex align="center" gap="12px">
          <Flex flex="none" w="32px" h="32px" borderRadius="8px" border="1px solid" borderColor={ff.border} align="center" justify="center" color={ff.ink}>
            <FiCalendar size={18} strokeWidth={1.8} aria-hidden />
          </Flex>
          <Box>
            <Text id="calendar-invites-label" fontSize="16px" fontWeight={600}>Calendar invites</Text>
            <Text fontSize="13px" color={ff.text2}>Add scheduled collabs to Google Calendar</Text>
          </Box>
        </Flex>
        <Switch
          aria-labelledby="calendar-invites-label"
          isChecked={!optOut}
          onChange={(e) => handleToggle(!e.target.checked)}
          isDisabled={isSaving}
          size="lg"
          flex="none"
        />
      </Flex>
    </Box>
  );
};

export default NotificationsEdit;
